import { DefaultNumberInputField } from '~/common/components/defaultUIComponents/DefaultNumberInputField';
import { CreateSpecialJobValuesSchema } from '~/common/validators/createSpecialJobValidator';
import { SpecialJobsCreateRequest } from '~/common/entities/SpecialJobs/SpecialJobRequest';
import { SelectField } from '~/common/components/defaultUIComponents/SelectField';
import { InputField } from '~/common/components/defaultUIComponents/InputField';
import SpecialJobApi from '~/common/api/zeiterfassungServerApi/SpecialJobApi';
import { Heading } from '~/common/components/defaultUIComponents/Heading';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { Button } from '~/common/components/defaultUIComponents/Button';
import { secondsToHoursMinutesSeconds } from '~/common/utils/timeUtils';
import { Grid, GridItem, useToast } from '@chakra-ui/react';
import { formatName } from '~/common/utils/employeeUtils';
import { useNavigate, useParams } from 'react-router-dom';
import routes from '~/common/constants/routes.json';
import React, { useMemo } from 'react';
import { Form, Formik } from 'formik';
import _ from 'lodash';

interface CreateSpecialJobValues {
  name: string;
  description: string;
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  amount: number;
  employeeId: string;
}

const CreateEditSpecialJob: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const { data: specialJob } = SpecialJobApi.hooks.useSpecialJobQuery(
    { id },
    {
      enabled: !!id,
    },
  );
  const { mutateAsync: editSpecialJob } =
    SpecialJobApi.hooks.useEditSpecialJobMutation();
  const { mutateAsync: createSpecialJob } =
    SpecialJobApi.hooks.useCreateSpecialJobMutation();

  const initialValues = useMemo(() => {
    const hoursMinutesSeconds = secondsToHoursMinutesSeconds(
      specialJob?.duration,
    );
    return {
      name: specialJob?.name ?? '',
      description: specialJob?.description ?? '',
      durationHours: hoursMinutesSeconds.hours,
      durationMinutes: hoursMinutesSeconds.minutes,
      durationSeconds: hoursMinutesSeconds.seconds,
      amount: specialJob?.amount ?? 0,
      employeeId: specialJob?.employee ?? '',
    } as CreateSpecialJobValues;
  }, [specialJob]);
  const toast = useToast({
    status: 'error',
    description: 'generic.error',
  });

  const handleSubmit = (values: CreateSpecialJobValues) => {
    const createValues: SpecialJobsCreateRequest = {
      name: values.name,
      description: values.description,
      duration:
        (values.durationHours * 60 * 60 +
          values.durationMinutes * 60 +
          values.durationSeconds) *
        values.amount,
      amount: values.amount,
      employee: values.employeeId,
    };
    if (id) {
      editSpecialJob({ id, ...createValues })
        .then(() => navigate(routes.SPECIAL_JOBS))
        .catch(() => toast({}));
    } else {
      createSpecialJob(createValues)
        .then(() => navigate(routes.SPECIAL_JOBS))
        .catch(() => toast({}));
    }
  };

  return (
    <>
      {!id ? (
        <Heading>createSpecialJob.heading</Heading>
      ) : (
        <Heading>editSpecialJob.heading</Heading>
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={CreateSpecialJobValuesSchema}
        validateOnChange
        enableReinitialize
      >
        {({ values, setValues }) => {
          if (values.durationSeconds > 60)
            void setValues({
              ...values,
              durationSeconds: 59,
            });
          else if (values.durationMinutes > 60)
            void setValues({
              ...values,
              durationMinutes: 59,
            });
          else if (
            values.durationSeconds > 59 &&
            values.durationMinutes >= 59
          ) {
            void setValues({
              ...values,
              durationSeconds: 0,
              durationMinutes: 0,
              durationHours: values.durationHours + 1,
            });
          } else if (values.durationMinutes > 59) {
            void setValues({
              ...values,
              durationMinutes: 0,
              durationHours: values.durationHours + 1,
            });
          } else if (values.durationSeconds > 59) {
            void setValues({
              ...values,
              durationSeconds: 0,
              durationMinutes: values.durationMinutes + 1,
            });
          }
          return (
            <Form>
              <Grid templateColumns="repeat(12, 1fr)" gap={5}>
                <GridItem colSpan={12}>
                  <InputField
                    name="name"
                    label="createSpecialJob.name.label"
                    placeholder=""
                    mt={1}
                  />
                </GridItem>
                <GridItem colSpan={4}>
                  <DefaultNumberInputField
                    name="amount"
                    label="createSpecialJob.amount.label"
                    mb={2}
                    min={1}
                    maxInputLenght={2}
                  />
                </GridItem>
                <GridItem colSpan={4}>
                  <DefaultNumberInputField
                    name="durationHours"
                    label="createSpecialJob.durationInHours.label"
                    min={0}
                  />
                </GridItem>
                <GridItem colSpan={4}>
                  <DefaultNumberInputField
                    name="durationMinutes"
                    label="createSpecialJob.durationInMinutes.label"
                    min={0}
                    maxInputLenght={2}
                  />
                </GridItem>
                <GridItem colSpan={12}>
                  <InputField
                    name="description"
                    label="createSpecialJob.description.label"
                    placeholder=""
                    mb={5}
                    textarea
                  />
                </GridItem>
                <GridItem colSpan={12}>
                  <SelectField
                    name="employeeId"
                    label="createSpecialJob.employee.label"
                    placeholder="createSpecialJob.employee.placeholder"
                    fields={_.orderBy(employees, formatName)}
                    keys={(e) => e.id}
                    labels={formatName}
                    values={(e) => e.id}
                    w="100%"
                  />
                </GridItem>
                <GridItem colSpan={12}>
                  <Button w="full" type="submit">
                    createSpecialJob.submit.text
                  </Button>
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CreateEditSpecialJob;
